import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, Camera, CheckSquare, MoreHorizontal,
  FolderOpen, Package, Users, UserCheck, TrendingUp, Briefcase,
  UserCircle, Settings, ShieldCheck, Handshake, X, Sun, Moon, Truck,
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermission';

const PRIMARY_TABS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/calendar',  icon: Calendar,         label: 'Calendario' },
  { to: '/events',    icon: Camera,            label: 'Eventi'   },
  { to: '/tasks',     icon: CheckSquare,       label: 'Assegnazioni'    },
];

const BASE_SECONDARY_TABS = [
  { to: '/finance',                icon: TrendingUp,  label: 'Finanza' },
  { to: '/performances',           icon: Briefcase,   label: 'Prestazioni' },
  { to: '/projects',               icon: FolderOpen,  label: 'Progetti' },
  { to: '/catalog',                icon: Package,     label: 'Catalogo' },
  { to: '/suppliers',              icon: Truck,       label: 'Fornitori' },
  { to: '/clients',                icon: Users,       label: 'Clienti' },
  { to: '/external-photographers', icon: UserCheck,   label: 'Esterni' },
  { to: '/collaborators',          icon: Handshake,   label: 'Collaboratori' },
  { to: '/profile',                icon: UserCircle,  label: 'Profilo' },
  { to: '/settings',               icon: Settings,    label: 'Impostazioni' },
];

interface MobileNavProps {
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
}

export function MobileNav({ theme, onToggleTheme }: MobileNavProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const canFinance = hasPermission('finance.view');
  const canPerformancesView = hasPermission('performances.view');
  const canPerformancesMyView = hasPermission('performances.my.view');
  const canPerformances = canPerformancesView || canPerformancesMyView;
  const canUsersManage = hasPermission('users.manage');
  const canSuppliers = hasPermission('suppliers.view');

  const secondaryTabs = [
    ...BASE_SECONDARY_TABS.filter(t => (t.to !== '/finance' || canFinance) && (t.to !== '/performances' || canPerformances) && (t.to !== '/suppliers' || canSuppliers)),
    ...(canUsersManage ? [{ to: '/users', icon: ShieldCheck, label: 'Gestione Utenti' }] : []),
  ];

  const anySecondaryActive = secondaryTabs.some(t => location.pathname.startsWith(t.to));

  return (
    <>
      <style>{`
        .lc-nav-bar {
          background: rgba(24,24,27,0.35);
          backdrop-filter: blur(24px) saturate(180%);
          -webkit-backdrop-filter: blur(24px) saturate(180%);
          border: 0.5px solid rgba(255,255,255,0.08);
          box-shadow: 0 4px 24px rgba(0,0,0,0.3);
        }
        @supports (-webkit-touch-callout: none) {
          .lc-nav-bar {
            background: rgba(24,24,27,0.88);
          }
        }
        [data-theme="light"] .lc-nav-bar {
          background: rgba(255,255,255,0.85);
          backdrop-filter: blur(24px) saturate(180%) brightness(1.06);
          -webkit-backdrop-filter: blur(24px) saturate(180%) brightness(1.06);
          border-color: rgba(0,0,0,0.08);
          box-shadow: 0 4px 24px rgba(0,0,0,0.08);
        }
        @supports (-webkit-touch-callout: none) {
          [data-theme="light"] .lc-nav-bar {
            background: rgba(255,255,255,0.92);
          }
        }

        .lc-nav-tab {
          color: #A1A1AA;
          transition: all 0.25s cubic-bezier(0.4,0,0.2,1);
          -webkit-tap-highlight-color: transparent;
        }
        .lc-nav-tab svg {
          stroke: #A1A1AA;
          transition: stroke 0.25s cubic-bezier(0.4,0,0.2,1);
        }
        .lc-nav-tab:not(.active):active {
          background: rgba(255,255,255,0.06);
        }
        .lc-nav-tab.active {
          background: #3E91FF;
          box-shadow: 0 2px 12px rgba(62,145,255,0.35);
          color: #fff;
        }
        .lc-nav-tab.active svg {
          stroke: #fff;
        }

        [data-theme="light"] .lc-nav-tab {
          color: #71717A;
        }
        [data-theme="light"] .lc-nav-tab svg {
          stroke: #71717A;
        }
        [data-theme="light"] .lc-nav-tab:not(.active):active {
          background: rgba(0,0,0,0.04);
        }
        [data-theme="light"] .lc-nav-tab.active {
          background: #3E91FF;
          box-shadow: 0 2px 12px rgba(62,145,255,0.25);
          color: #fff;
        }
        [data-theme="light"] .lc-nav-tab.active svg {
          stroke: #fff;
        }

        /* Altro sheet */
        .lc-sheet-overlay {
          transition: opacity 280ms cubic-bezier(0.16,1,0.3,1);
        }
        .lc-sheet {
          transition: transform 320ms cubic-bezier(0.16,1,0.3,1);
        }
        [data-theme="light"] .lc-sheet {
          background: rgba(255,255,255,0.92) !important;
        }
        [data-theme="light"] .lc-sheet-item {
          background: rgba(0,0,0,0.03) !important;
          border-color: rgba(0,0,0,0.06) !important;
        }
        [data-theme="light"] .lc-sheet-item:hover {
          background: rgba(62,145,255,0.08) !important;
        }
        [data-theme="light"] .lc-sheet-item svg {
          color: #71717A !important;
        }
        [data-theme="light"] .lc-sheet-item span {
          color: #71717A !important;
        }
      `}</style>

      {/* Overlay */}
      <div
        onClick={() => setMoreOpen(false)}
        className="lc-sheet-overlay"
        style={{
          position: 'fixed', inset: 0, zIndex: 199,
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          opacity: moreOpen ? 1 : 0,
          pointerEvents: moreOpen ? 'auto' : 'none',
        }}
      />

      {/* Sheet "Altro" */}
      <div
        className="lc-sheet"
        style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 200,
          transform: moreOpen ? 'translateY(0)' : 'translateY(110%)',
          background: 'rgba(24,24,27,0.40)',
          backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          borderRadius: '22px 22px 0 0',
          borderTop: '0.5px solid rgba(255,255,255,0.08)',
          paddingTop: '0.5rem',
          boxShadow: '0 -8px 32px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{
          width: 36, height: 4,
          background: 'rgba(255,255,255,0.2)',
          borderRadius: 9999,
          margin: '8px auto 12px',
        }} />

        <button
          onClick={() => setMoreOpen(false)}
          style={{
            position: 'absolute', top: 12, right: 12,
            width: 32, height: 32, borderRadius: 9999,
            background: 'rgba(255,255,255,0.06)',
            border: '0.5px solid rgba(255,255,255,0.10)',
            color: 'var(--color-text-muted)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <X size={16} />
        </button>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12,
          padding: '20px 16px',
          paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
        }}>
          {secondaryTabs.map(tab => {
            const Icon = tab.icon;
            const active = location.pathname.startsWith(tab.to);
            return (
              <NavLink
                key={tab.to}
                to={tab.to}
                onClick={() => setMoreOpen(false)}
                className="lc-sheet-item"
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  padding: '14px 8px', borderRadius: 14,
                  background: active ? 'rgba(62,145,255,0.10)' : 'rgba(255,255,255,0.04)',
                  border: active ? '0.5px solid rgba(62,145,255,0.25)' : '0.5px solid rgba(255,255,255,0.06)',
                  textDecoration: 'none',
                  transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)',
                }}
              >
                <Icon size={28} strokeWidth={1.5}
                  style={{ color: active ? '#60A5FA' : '#A1A1AA', transition: 'all 0.2s ease' }}
                />
                <span style={{
                  fontSize: '0.7rem', fontWeight: active ? 600 : 400,
                  color: active ? '#60A5FA' : 'var(--color-text-muted)',
                  textAlign: 'center',
                }}>
                  {tab.label}
                </span>
              </NavLink>
            );
          })}
          <button
            onClick={() => { onToggleTheme(); setMoreOpen(false); }}
            className="lc-sheet-item"
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
              padding: '14px 8px', borderRadius: 14,
              background: 'rgba(255,255,255,0.04)',
              border: '0.5px solid rgba(255,255,255,0.06)',
              cursor: 'pointer',
              transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            {theme === 'dark'
              ? <Sun size={28} strokeWidth={1.5} color="#A1A1AA" />
              : <Moon size={28} strokeWidth={1.5} color="#A1A1AA" />
            }
            <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
              {theme === 'dark' ? 'Chiaro' : 'Scuro'}
            </span>
          </button>
        </div>
      </div>

      {/* Bottom navigation bar — Telegram-style floating pill */}
      <nav
        className="lc-nav-bar"
        aria-label="Navigazione principale"
        style={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          zIndex: 150,
          borderRadius: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          padding: '4px 8px',
          height: 56,
          willChange: 'transform',
          transform: 'translateZ(0)',
        }}
      >
        {PRIMARY_TABS.map(tab => {
          const Icon = tab.icon;
          const active = location.pathname.startsWith(tab.to);
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={`lc-nav-tab${active ? ' active' : ''}`}
              style={{
                position: 'relative',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                gap: 2, padding: '6px 16px',
                borderRadius: 9999, minWidth: 56, height: 44,
                textDecoration: 'none', fontSize: 10, fontWeight: active ? 600 : 500,
                lineHeight: 1,
              }}
            >
              <Icon size={22} strokeWidth={1.5} style={{ fill: 'none' }} />
              {tab.label}
            </NavLink>
          );
        })}

        {/* Altro */}
        <button
          onClick={() => setMoreOpen(o => !o)}
          className={`lc-nav-tab${(moreOpen || anySecondaryActive) ? ' active' : ''}`}
          style={{
            position: 'relative',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: 2, padding: '6px 16px',
            borderRadius: 9999, minWidth: 56, height: 44,
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 10, fontWeight: (moreOpen || anySecondaryActive) ? 600 : 500,
            lineHeight: 1,
          }}
        >
          <MoreHorizontal size={22} strokeWidth={1.5} style={{ fill: 'none' }} />
          Altro
        </button>
      </nav>
    </>
  );
}
